import type { PayloadAction } from '@reduxjs/toolkit';
import { all, put, select, takeLatest, delay } from 'redux-saga/effects';

import RestClient from '~/modules/requests';

import {
  getFlightOffers,
  getFlightOffersPriceConfimation,
  getFlightsFailure,
  getFlightsSuccess,
  getFlightOfferBookingDetailsSuccess,
  getFlightsPriceConfimationFailure,
  setPriceCheckFromSearchResult,
  getFlightsPriceConfimationSuccess,
  resetBookingData,
  filterOffers,
  filterFailure,
  filterSuccess,
  lazyLoadOffers,
  lazyLoadOffersSucess,
} from '~/actions';
import { ApiResponse, FlightSearchFilters, FlightSearchRequestParams, Offer, Paging, PriceConfimationParameters } from '~/types';
import { FlightSearchResponse } from '~/types/flights-search';
import { CONFIG } from '~/config';
import { convertToPriceCheckResponse } from '~/modules/helpers';
import { filterByAirlines, filterByBaggage, filterByDepartureTimeRange, filterByPriceRange, filterByStopoverDuration, filterByStops, getOffersSummary } from './helpers';
import { MAX_OFFERS, PAGE_SIZE } from '~/literals';

/**
 * Get Flights
 *
 * @param {Object} action
 *
 */

export function* getFlightOffersSaga({ payload }: PayloadAction<FlightSearchRequestParams>) {
  try {
    const response: ApiResponse<FlightSearchResponse> = yield RestClient.get(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/offers?${new URLSearchParams({ ...payload, maxOffers: MAX_OFFERS } as Record<string, any>).toString()}`,
    );

    if (response.status === 200) {
      const flightSearchResponse = response.data as FlightSearchResponse;
      yield put(resetBookingData());
      yield put(
        getFlightsSuccess({
          ...flightSearchResponse,
          offerSummary: getOffersSummary(flightSearchResponse.offers),
        }
        ));
    } else {
      const errorResponse = response?.data as any;
      yield put(getFlightsFailure({
        errorMessage: errorResponse?.error || 'Failed to fetch flight offers.',
      }));
    }
  } catch (error: any) {
    yield put(resetBookingData());
    yield put(getFlightsFailure({
      errorMessage: error?.response?.data?.error || error.message
    }));
  }
}

export function* filterFlightOffersSaga({ payload }: PayloadAction<FlightSearchFilters>) {
  try {
    yield delay(2000);
    const allOffers: Offer[] = yield select(select => select.flightSearch.flightSearchRespose?.offers);

    let filteredOffers = [...allOffers];

    if (payload.stops) {
      filteredOffers = filterByStops(filteredOffers, payload.stops);
    }

    if (payload.airlines) {
      filteredOffers = filterByAirlines(filteredOffers, payload.airlines);
    }

    if (payload.baggage) {
      filteredOffers = filterByBaggage(filteredOffers, payload.baggage);
    }

    if (payload.departureTimeRange) {
      filteredOffers = filterByDepartureTimeRange(filteredOffers, payload.departureTimeRange);
    }

    if (payload.stopoverDuration) {
      filteredOffers = filterByStopoverDuration(filteredOffers, payload.stopoverDuration);
    }

    if (payload.priceRange) {
      filteredOffers = filterByPriceRange(filteredOffers, payload.priceRange);
    }

    yield put(filterSuccess(filteredOffers));
  } catch (error: any) {
    yield put(filterFailure(error.message));
  }
}


export function* getFlightOffersPriceConfimationSaga({ payload }: PayloadAction<PriceConfimationParameters>) {
  try {
    const response: ApiResponse<any> = yield RestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/flight-booking/confirm-offer-price`,
      payload
    );

    if (response.status === 200) {
      if (response.data) {
        yield put(getFlightsPriceConfimationSuccess());
        yield put(setPriceCheckFromSearchResult());
        yield put(getFlightOfferBookingDetailsSuccess(convertToPriceCheckResponse(response.data)));
      }
    } else {
      yield put(getFlightsPriceConfimationFailure(
        {
          errorMessage: `${response.data.error}`,
        }
      ));
    }
  } catch (error: any) {
    yield put(getFlightsPriceConfimationFailure(
      {
        errorMessage: `${error}`,
      }
    ));
  }
}

function* lazyLoadOffersSaga({ payload }: PayloadAction<Paging>) {
  delay(1000)
  const offersToView: Offer[] = yield select(select => select.flightSearch.offersToView);
  const lazyLoadedOffers: Offer[] = yield select(select => select.flightSearch.lazyLoadedOffers);

  const startIndex = (payload.currentPage || 0) * (payload.pageSize || PAGE_SIZE);
  const endIndex = startIndex + (payload.pageSize || PAGE_SIZE);
  const nextItems = offersToView.slice(startIndex, endIndex);
  const offers = [...lazyLoadedOffers, ...nextItems];
  yield put(lazyLoadOffersSucess(offers));
}

/**
 * Flights Sagas
 */
export default function* root() {
  yield all([
    takeLatest(getFlightOffers.type, getFlightOffersSaga),
    takeLatest(filterOffers.type, filterFlightOffersSaga),
    takeLatest(getFlightOffersPriceConfimation.type, getFlightOffersPriceConfimationSaga),
    takeLatest(getFlightsSuccess.type, lazyLoadOffersSaga),
    takeLatest(filterSuccess.type, lazyLoadOffersSaga),
    takeLatest(lazyLoadOffers.type, lazyLoadOffersSaga),
  ]);
}
